import { useEffect, useRef, useState } from "react";
import { Diagram } from "./diagram/Diagram";
import { useAppDispatch, useAppSelector, useInterval } from '../../hooks/hooks';
import { Toolbar } from "./toolbar/Toolbar";
import "./Editor.scss";
import { Catalog } from "./catalog/Catalog";
import { useGraph } from "../../hooks/useGraph";
import { useLazyGetTopologyQuery } from '../../app/apiSlice';
import { useLocation } from "react-router-dom";
import { PipelineState, setTopology, setTopologyData } from './editorSlice';
import { Sidebar } from "./sidebar/Sidebar";
import { FloatPane } from "./FloatPane";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setChangedTopology, resetEditorSlice, setZoom } from "./editorSlice";

import { useTranslation } from "react-i18next";
import { dagdifferences } from "../../utils/dagdifferences";
import { correspondenceBoxIdName } from "../../utils/correspondenceBoxIdName";
import SplitPane from "react-split-pane";
import "./Split.scss";
import { useLazyGetCatalogQuery } from "../libraries/DataLibrarySlice";
import { Zoom } from "./zoom/zoom";
import { TopBarEditor } from "./topBar/topBarEditor";
import { LogsDatas } from "./logs/LogsDatas";
import { useSecureApplication } from "../../hooks/KeycloackConnection";
import { useRedirect404 } from "../../hooks/useRedirect404";
import { useSavePipeline } from '../../hooks/useSavePipeline';
import { Dialog } from "primereact/dialog";
import { CodePanel } from "./CodePanel/CodePanel";
import { StepByStep, StepByStepApi } from "../../components/StepByStep/StepByStep";
import { setToast } from '../../app/appSlice';

export const Editor = () => {
  const search = useLocation()?.search;
  const dispatch = useAppDispatch();
  const pipelineChanged = useAppSelector((state) => state.editor.changedTopology);
  const restoredTopology = useAppSelector((state) => state.editor.restoredTopology);
  const { savePipeline } = useSavePipeline();
  const id = new URLSearchParams(search).get("t") as string;
  const version = new URLSearchParams(search).get("v") as string;
  const [getCatalog, { data: catalog }] = useLazyGetCatalogQuery();
  const [getTopology, { data: topology, error }] = useLazyGetTopologyQuery();
  const pipelineState = useAppSelector((state) =>  state.editor.pipelineState);
  const {t: translate } = useTranslation();

  useRedirect404(error, "treatments");
  const streamNameAndRunID = useAppSelector(
    (state) => state.editor.streamNameAndRunID
  );
  const {
    sidebar: sb,
    sizePanel,
    debugMode,
  } = useAppSelector((state) => state.editor);

  const {
    engine,
    load,
    isReady,
    newDiagram,
    deleteDiagram,
    clearDiagram,
    activeNode,
    render
  } = useGraph();
  const element = useRef<any>(null);
  const eltStepByStep = useRef<any>(null);
  const [catalogVisible, setCatalogVisible] = useState(true);
  const { hasAnyPerm } = useSecureApplication();
  const [codePanelVisible, setCodePanelVisible] = useState<boolean>(false);
  const [checkedOnce, setCheckedOnce] = useState(false);

  useInterval(async () => {
    if (hasAnyPerm('pipelines.all', 'pipelines_upd') && pipelineChanged) {
      await savePipeline(topology, catalog, topology?.version);
    }
  }, 300_000);

  useEffect(() => {
    dispatch(setZoom(100));
    getTopology({id, version});

    return () => {
      /* When the component is unmount then initialise topology and runtime store */
      dispatch(resetEditorSlice());
      deleteDiagram();
    };
  }, []);

  // check for box update when starting editor page
  useEffect(() => {
    if (topology && catalog && engine && !checkedOnce) {
      let flowRender = undefined;
      if (catalog) flowRender = render({
        hidePassword: false,
        catalog: catalog,
      })
      if(flowRender && flowRender.length > 0) {
        const dagdiff = dagdifferences(JSON.stringify(flowRender),topology.data)
        if (dagdiff.length > 0) {
          const mapBoxIdName = correspondenceBoxIdName(topology.graph)
          const names = mapBoxIdName
          .filter(box =>
            dagdiff.includes(box.id) &&
            box.name !== "DELETED-BOX"
          )
          .map(box => box.name)
          .join(', ');
          dispatch(setToast({
            severity: "warning",
            content: translate('message.yamlchangederror') + names,
            life: 8_000
        }));
          dispatch(setChangedTopology(true));
        }
        setCheckedOnce(true);
      }
    }
  }, [topology, catalog, checkedOnce, engine, dispatch]);

  useEffect(() => {
    if (topology) {
      if (!catalog) {
        getCatalog({ runtimeType: topology.runtimeMode },true);
      } else {
        dispatch(setTopology({...topology}));
        dispatch(setTopologyData(topology.data));
        // Create new diagram if engine not instantiated
        if (!engine) {
          newDiagram();
        }
        // Load diagram if not already done
        if (!isReady) {
          load(topology.graph, catalog)
        }
      }
    }
  }, [topology, catalog]);

  useEffect(() => {
    if (restoredTopology) {
      clearDiagram()
      getTopology({id, version});
    }
  }, [restoredTopology]);

  if (topology === undefined || !isReady) {
    return <div className="editor flex "></div>;
  }

  const renderCatalog = () => {
    return (
      <FloatPane
        icon={<FontAwesomeIcon icon="puzzle-piece" />}
        position={"left"}
        visible={catalogVisible && (pipelineState === PipelineState.DELETED || pipelineState === PipelineState.STOPPED || pipelineState === PipelineState.COMPLETED || pipelineState === PipelineState.FAILED)}
        onHide={() => setCatalogVisible(false)}
        onVisible={() => setCatalogVisible(true)}
      >
        <Catalog />
      </FloatPane>
    );
  };

  const handleToolbar = (buttonId: string) => {
    switch(buttonId) {
      case 'code':
        setCodePanelVisible(true);
        break;
      case 'debug':
        if (debugMode === false) {
          (eltStepByStep.current as StepByStepApi).load(undefined);
        }
        break;
      default:
        break;
    }
  }

  return (
    <div className="editor flex" ref={element}>
      {renderCatalog()}

      {topology && <div className="editor-main flex flex-column">
        <TopBarEditor onButtonClick={handleToolbar}/>
        <Toolbar topology={topology} onButtonClick={handleToolbar}/>

        <div className="editor-main-pane flex flex-row">
          {/* with the propriety paneStyle you can apply the css style to all Pane */}
          <SplitPane
            split="vertical"
            paneStyle={{ overflow: "scroll" }}
            minSize={243}
            maxSize={1700}
            size={sizePanel}
            pane1Style={{ flex: "initial" }}
            pane2Style={{
              flex: `${sizePanel === "100%" ? "1 0 0px" : "1 0 400px"}`,
            }}
            allowResize={sizePanel !== "100%"}
          >
            {/* Left Pane - with console*/}
            {streamNameAndRunID && hasAnyPerm("flows.all", "flows_teclogs") ? (
              <SplitPane
                allowResize={true}
                defaultSize="25%"
                minSize={0}
                split="horizontal"
                primary="second"
              >
                {/* Up (left) pane */}
                <div style={{ width: "100%", height: "100%" }}>
                  <Diagram />
                  <Zoom />
                </div>

                {/* Down (left) pane */}
                {topology.environnement && (
                  <LogsDatas
                    streamNameAndRunID={streamNameAndRunID}
                    env={topology.environnement}
                  />
                )}
              </SplitPane>
            ) : (
              /* Left Pane - without console*/
              <div style={{ width: "100%", height: "100%" }}>
                <Diagram />
                <Zoom />
              </div>
            )}

            {/* Right pane */}
            {/*<div style={{ overflow: "scroll!important" }}>*/}
            <Sidebar pane={sb.pane} arg={sb.arg} />
            {/*</div>*/}
          </SplitPane>
          <Dialog
            visible={codePanelVisible}
            header={'Code du Traitement'}
            onHide={() => setCodePanelVisible(false)}
            style={{ width: '50vw', height: '80vh' }}
          >
            <CodePanel/>
          </Dialog>
          <StepByStep ref={eltStepByStep} topology={topology}/>
        </div>
      </div>}
    </div>
  );
};
